import React from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ROLE_LABEL } from "@/lib/format";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";

export default function UsersPage() {
  const [rows, setRows] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const [form, setForm] = React.useState({ email: "", name: "", role: "sales", password: "", active: true });
  const [pwUser, setPwUser] = React.useState(null);
  const [pwForm, setPwForm] = React.useState({ password: "", confirm: "" });

  const load = React.useCallback(async () => { const { data } = await api.get("/users"); setRows(data); }, []);
  React.useEffect(() => { load(); }, [load]);

  const save = async () => {
    try { await api.post("/users", form); toast.success("Pengguna dibuat"); setOpen(false); setForm({ email: "", name: "", role: "sales", password: "", active: true }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleActive = async (u) => {
    try { await api.patch(`/users/${u.id}`, { active: !u.active }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const openResetPassword = (u) => { setPwUser(u); setPwForm({ password: "", confirm: "" }); };
  const submitResetPassword = async () => {
    if (!pwForm.password || pwForm.password.length < 6) return toast.error("Password minimal 6 karakter");
    if (pwForm.password !== pwForm.confirm) return toast.error("Konfirmasi password tidak cocok");
    try {
      await api.patch(`/users/${pwUser.id}`, { password: pwForm.password });
      toast.success(`Password ${pwUser.name} berhasil direset`);
      setPwUser(null);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="users-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>Pengguna & Hak Akses</h1>
          <p className="text-sm text-neutral-500">Kelola akun staf dan role.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-green-600 hover:bg-green-700" data-testid="btn-new-user">Pengguna Baru</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Pengguna Baru</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Nama</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name" /></div>
              <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email" /></div>
              <div><Label>Role</Label>
                <select className="w-full border border-neutral-200 rounded-md px-3 py-2 text-sm" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="user-role">
                  {Object.entries(ROLE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div><Label>Password</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password" /></div>
            </div>
            <DialogFooter><Button onClick={save} className="bg-green-600 hover:bg-green-700" data-testid="save-user">Simpan</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="card-flat overflow-x-auto">
        <table className="w-full data-table">
          <thead><tr>
            <th className="text-left">Nama</th>
            <th className="text-left">Email</th>
            <th className="text-left">Role</th>
            <th className="text-left">Status</th>
            <th></th>
          </tr></thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`}>
                <td>{u.name}</td>
                <td className="text-xs">{u.email}</td>
                <td className="text-xs">{ROLE_LABEL[u.role] || u.role}</td>
                <td>{u.active === false
                  ? <span className="text-xs px-2 py-0.5 rounded-full bg-neutral-100 border border-neutral-200 text-neutral-500">Nonaktif</span>
                  : <span className="text-xs px-2 py-0.5 rounded-full bg-green-50 border border-green-200 text-green-700">Aktif</span>}
                </td>
                <td className="text-right whitespace-nowrap">
                  <button onClick={() => openResetPassword(u)} className="text-xs text-green-700 hover:underline mr-3" data-testid={`reset-pw-${u.id}`}>Reset Password</button>
                  <button onClick={() => toggleActive(u)} className="text-xs text-amber-600 hover:underline" data-testid={`toggle-user-${u.id}`}>{u.active === false ? "Aktifkan" : "Nonaktifkan"}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!pwUser} onOpenChange={(v) => !v && setPwUser(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password · {pwUser?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-neutral-500">Setel ulang password untuk <b>{pwUser?.email}</b>. User akan menggunakan password baru pada login berikutnya.</p>
            <div>
              <Label>Password Baru</Label>
              <Input type="password" value={pwForm.password} onChange={(e) => setPwForm({ ...pwForm, password: e.target.value })} data-testid="reset-pw-new" placeholder="Minimal 6 karakter" />
            </div>
            <div>
              <Label>Konfirmasi Password Baru</Label>
              <Input type="password" value={pwForm.confirm} onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })} data-testid="reset-pw-confirm" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwUser(null)}>Batal</Button>
            <Button onClick={submitResetPassword} className="bg-green-600 hover:bg-green-700" data-testid="reset-pw-submit">Simpan Password Baru</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
